import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { dossierApi, imageUrl, PLACEHOLDER_IMAGE, API_BASE, BACKEND_URL } from "../lib/api";
import Logo from "../components/Logo";
import { Check, FileText, CalendarClock, Send, Loader2, Car, MessageCircle, Info } from "lucide-react";

const fmtDate = (d, loc) => (d ? new Date(d).toLocaleDateString(loc, { day: "2-digit", month: "long", year: "numeric" }) : null);
const fmtDateTime = (d, loc) => (d ? new Date(d).toLocaleString(loc, { dateStyle: "long", timeStyle: "short" }) : null);

const LOCALES = { nl: "nl-BE", fr: "fr-BE", en: "en-GB" };

const STEP_LABELS = {
  nl: {
    bestelbon: "Bestelbon getekend", voorschot: "Voorschot ontvangen", keuring_planning: "Keuring ingepland",
    keuring_voltooid: "Keuring voltooid", saldo: "Saldobetaling ontvangen", extra_werken: "Extra werken",
    aflevermoment: "Aflevermoment ingepland", afgeleverd: "Afgeleverd",
  },
  fr: {
    bestelbon: "Bon de commande signé", voorschot: "Acompte reçu", keuring_planning: "Contrôle technique planifié",
    keuring_voltooid: "Contrôle technique terminé", saldo: "Paiement du solde reçu", extra_werken: "Travaux supplémentaires",
    aflevermoment: "Moment de livraison planifié", afgeleverd: "Livré",
  },
  en: {
    bestelbon: "Order form signed", voorschot: "Deposit received", keuring_planning: "Inspection scheduled",
    keuring_voltooid: "Inspection completed", saldo: "Balance payment received", extra_werken: "Additional work",
    aflevermoment: "Delivery appointment scheduled", afgeleverd: "Delivered",
  },
};

const T = {
  nl: {
    unavailable: "Dit dossier is niet (meer) beschikbaar. Neem gerust contact op met TR Premium Cars.",
    yourPurchase: "Uw aankoop", fileOf: "Dossier van", ofSteps: (a, b) => `${a} van ${b} stappen`,
    statusTitle: "Status van uw dossier", inProgress: "Bezig",
    between: (a, b) => `Tussen ${a} en ${b}`, planned: (d) => `Gepland: ${d}`,
    questions: "Vragen of opmerkingen", namePh: "Uw naam (optioneel)", msgPh: "Stel hier uw vraag...",
  },
  fr: {
    unavailable: "Ce dossier n'est pas (plus) disponible. N'hésitez pas à contacter TR Premium Cars.",
    yourPurchase: "Votre achat", fileOf: "Dossier de", ofSteps: (a, b) => `${a} sur ${b} étapes`,
    statusTitle: "Statut de votre dossier", inProgress: "En cours",
    between: (a, b) => `Entre le ${a} et le ${b}`, planned: (d) => `Planifié : ${d}`,
    questions: "Questions ou remarques", namePh: "Votre nom (facultatif)", msgPh: "Posez votre question ici...",
  },
  en: {
    unavailable: "This file is no longer available. Feel free to contact TR Premium Cars.",
    yourPurchase: "Your purchase", fileOf: "File of", ofSteps: (a, b) => `${a} of ${b} steps`,
    statusTitle: "Status of your file", inProgress: "In progress",
    between: (a, b) => `Between ${a} and ${b}`, planned: (d) => `Scheduled: ${d}`,
    questions: "Questions or comments", namePh: "Your name (optional)", msgPh: "Ask your question here...",
  },
};

const REG = {
  nl: {
    title: "Inschrijving van uw voertuig",
    onlineTitle: "Online registratie (WEBDIV)",
    onlineMsg: "Het roze formulier is beschikbaar voor online registratie via WEBDIV. Uw verzekeringsmakelaar kan de inschrijving online uitvoeren.",
    manueelTitle: "Manuele registratie (DIV)",
    manueelIntro: "De documenten dienen manueel ingeschreven te worden via DIV. Kies hieronder hoe u dit wenst te regelen:",
    chosen: "Uw keuze",
    opts: [
      "Stuur het verzekeringsvignet + info voor het roze formulier naar ons (Wichelsesteenweg 100, 9340 Lede). Wij dienen het dossier persoonlijk in bij DIV Gent. (snelste & veiligste optie)",
      "De documenten worden na de keuring aangetekend naar mij (de klant) opgestuurd.",
      "De documenten worden na de keuring aangetekend naar mijn verzekeringsmakelaar opgestuurd.",
      "Ik kom de documenten zelf ophalen bij TR Premium Cars.",
    ],
    plateTitle: "Nummerplaatcombinatie",
    plateHint: "Vul hier de gewenste nummerplaatcombinatie in indien deze door ons besteld moet worden.",
    platePh: "bv. 1-ABC-123",
    plateSaved: "Combinatie doorgegeven — bedankt!",
    priceInfo: "De achterste nummerplaat + inschrijvingsdocumenten kosten € 30. Een duplicaat kost € 20.",
  },
  fr: {
    title: "Immatriculation de votre véhicule",
    onlineTitle: "Immatriculation en ligne (WEBDIV)",
    onlineMsg: "Le formulaire rose est disponible pour l'immatriculation en ligne via WEBDIV. Votre courtier en assurances peut effectuer l'immatriculation en ligne.",
    manueelTitle: "Immatriculation manuelle (DIV)",
    manueelIntro: "Les documents doivent être immatriculés manuellement via la DIV. Choisissez ci-dessous comment procéder :",
    chosen: "Votre choix",
    opts: [
      "Envoyez la vignette d'assurance + les infos pour le formulaire rose à notre adresse (Wichelsesteenweg 100, 9340 Lede). Nous déposons le dossier personnellement à la DIV de Gand. (option la plus rapide et sûre)",
      "Les documents me sont envoyés (au client) par recommandé après le contrôle technique.",
      "Les documents sont envoyés à mon courtier en assurances par recommandé après le contrôle technique.",
      "Je viens chercher les documents moi-même chez TR Premium Cars.",
    ],
    plateTitle: "Combinaison de plaque",
    plateHint: "Indiquez ici la combinaison de plaque souhaitée si elle doit être commandée par nos soins.",
    platePh: "ex. 1-ABC-123",
    plateSaved: "Combinaison transmise — merci !",
    priceInfo: "La plaque arrière + les documents d'immatriculation coûtent 30 €. Un duplicata coûte 20 €.",
  },
  en: {
    title: "Registration of your vehicle",
    onlineTitle: "Online registration (WEBDIV)",
    onlineMsg: "The pink form is available for online registration via WEBDIV. Your insurance broker can complete the registration online.",
    manueelTitle: "Manual registration (DIV)",
    manueelIntro: "The documents must be registered manually via DIV. Choose below how you would like to proceed:",
    chosen: "Your choice",
    opts: [
      "Send the insurance vignette + info for the pink form to us (Wichelsesteenweg 100, 9340 Lede). We submit the file personally at DIV Ghent. (fastest & safest option)",
      "The documents are sent to me (the customer) by registered mail after the inspection.",
      "The documents are sent to my insurance broker by registered mail after the inspection.",
      "I will pick up the documents myself at TR Premium Cars.",
    ],
    plateTitle: "Number plate combination",
    plateHint: "Enter the desired number plate combination here if it needs to be ordered by us.",
    platePh: "e.g. 1-ABC-123",
    plateSaved: "Combination submitted — thank you!",
    priceInfo: "The rear number plate + registration documents cost € 30. A duplicate costs € 20.",
  },
};

export default function DossierStatus() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [choosingReg, setChoosingReg] = useState(false);
  const [plateInput, setPlateInput] = useState("");
  const [savingPlate, setSavingPlate] = useState(false);
  const [plateSaved, setPlateSaved] = useState(false);
  const [showPriceInfo, setShowPriceInfo] = useState(false);

  const load = () => {
    dossierApi.getPublic(token).then(setD).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  useEffect(() => {
    if (!d) return;
    const kv = (d.steps || []).find((s) => s.key === "keuring_voltooid");
    if (kv?.plate_combination && !plateInput) setPlateInput(kv.plate_combination);
    /* eslint-disable-next-line */
  }, [d]);

  const savePlate = async () => {
    if (!plateInput.trim()) return;
    setSavingPlate(true);
    try {
      await dossierApi.setPlateCombination(token, plateInput.trim());
      setPlateSaved(true);
    } catch (e) {
      /* ignore */
    } finally {
      setSavingPlate(false);
    }
  };

  const chooseReg = async (n) => {
    setChoosingReg(true);
    try {
      await dossierApi.setRegistrationChoice(token, String(n));
      load();
    } catch (e) {
      /* ignore */
    } finally {
      setChoosingReg(false);
    }
  };

  const submitComment = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await dossierApi.addCustomerComment(token, { name: name.trim() || null, text: text.trim() });
      setD((prev) => ({ ...prev, comments: [...(prev.comments || []), res.comment] }));
      setText("");
    } catch (e) {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#c5a76f] animate-spin" /></div>;

  if (error || !d) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white flex flex-col items-center justify-center px-6 text-center">
        <Logo />
        <p className="mt-6 text-white/60 max-w-md">This file is no longer available · Ce dossier n'est plus disponible · Dit dossier is niet meer beschikbaar.</p>
        <a href="https://tr-premiumcars.be" className="mt-4 text-[#c5a76f] hover:underline text-sm">tr-premiumcars.be</a>
      </div>
    );
  }

  const pct = Math.round((d.progress.completed / d.progress.total) * 100);
  const currentKey = d.progress.current;
  const lang = T[d.language] ? d.language : "nl";
  const t = T[lang];
  const loc = LOCALES[lang];
  const labels = STEP_LABELS[lang];

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-5 py-4 flex items-center justify-center">
        <Logo />
      </header>

      <div className="max-w-2xl mx-auto px-5 py-8">
        {/* Car hero */}
        <div className="rounded-xl overflow-hidden border border-white/8 bg-[#242424]">
          <div className="aspect-[16/9] bg-[#1a1a1a]">
            <img src={d.car_image ? imageUrl(d.car_image) : PLACEHOLDER_IMAGE} alt={d.car_title} className="w-full h-full object-cover" />
          </div>
          <div className="p-5">
            <div className="text-[#c5a76f] text-xs tracking-widest uppercase flex items-center gap-2">
              <Car className="w-3.5 h-3.5" /> {t.yourPurchase}
            </div>
            <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: "'Familjen Grotesk', serif" }}>{d.car_title}</h1>
            {d.customer_name && <p className="text-white/50 text-sm mt-1">{t.fileOf} {d.customer_name}</p>}
            {/* Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-white/40 mb-1.5">
                <span>{t.ofSteps(d.progress.completed, d.progress.total)}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#c5a76f] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-8">
          <h2 className="text-white/80 text-sm uppercase tracking-widest mb-4">{t.statusTitle}</h2>
          <ol className="relative">
            {d.steps.map((s, idx) => {
              const done = s.status === "completed";
              const current = !done && s.key === currentKey;
              const last = idx === d.steps.length - 1;
              return (
                <li key={s.key} className="relative pl-12 pb-7" data-testid={`pub-step-${s.key}`}>
                  {!last && <span className={`absolute left-[15px] top-8 bottom-0 w-px ${done ? "bg-[#c5a76f]/50" : "bg-white/10"}`} />}
                  <span className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                    done ? "bg-[#c5a76f] border-[#c5a76f] text-black"
                    : current ? "border-[#c5a76f] text-[#c5a76f] bg-[#1a1a1a]"
                    : "border-white/15 text-white/40 bg-[#1a1a1a]"}`}>
                    {done ? <Check className="w-4 h-4" /> : idx + 1}
                  </span>
                  <div className={`pt-1 ${done || current ? "" : "opacity-55"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${current ? "text-[#c5a76f]" : "text-white"}`}>{labels[s.key] || s.label}</span>
                      {current && <span className="text-[10px] uppercase tracking-wider text-[#c5a76f] border border-[#c5a76f]/40 rounded-full px-2 py-0.5">{t.inProgress}</span>}
                    </div>
                    {done && s.completed_at && <div className="text-white/40 text-xs mt-0.5">{fmtDate(s.completed_at, loc)}</div>}

                    {/* Step-specific public info */}
                    {s.key === "keuring_planning" && (s.date_from || s.date_to) && (
                      <div className="text-white/60 text-xs mt-1">
                        {s.date_from && s.date_to ? t.between(fmtDate(s.date_from, loc), fmtDate(s.date_to, loc)) : t.planned(fmtDate(s.date_from || s.date_to, loc))}
                      </div>
                    )}
                    {s.key === "keuring_voltooid" && (s.documents || []).length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {s.documents.map((doc) => (
                          <a key={doc.id} href={`${BACKEND_URL}${doc.url}${doc.url.includes("?") ? "&" : "?"}token=${token}`} target="_blank" rel="noreferrer"
                            data-testid={`pub-doc-${doc.id}`}
                            className="inline-flex items-center gap-2 text-xs text-[#c5a76f] hover:underline bg-[#c5a76f]/10 border border-[#c5a76f]/20 rounded px-3 py-1.5">
                            <FileText className="w-3.5 h-3.5" /> {doc.name}
                          </a>
                        ))}
                      </div>
                    )}
                    {s.key === "keuring_voltooid" && s.registration_type && (
                      <div className="mt-3 rounded-lg border border-white/10 bg-[#242424] p-3" data-testid="pub-registration">
                        <div className="text-white/80 text-xs font-semibold mb-1.5">{REG[lang].title}</div>
                        {s.registration_type === "online" && (
                          <div>
                            <div className="text-[#c5a76f] text-xs font-medium mb-1">{REG[lang].onlineTitle}</div>
                            <p className="text-white/60 text-xs leading-relaxed">{REG[lang].onlineMsg}</p>
                          </div>
                        )}
                        {s.registration_type === "manueel" && (
                          <div>
                            <div className="text-[#c5a76f] text-xs font-medium mb-1">{REG[lang].manueelTitle}</div>
                            <p className="text-white/60 text-xs leading-relaxed mb-2">{REG[lang].manueelIntro}</p>
                            <div className="space-y-1.5">
                              {REG[lang].opts.map((o, i) => {
                                const n = String(i + 1);
                                const active = String(s.registration_choice) === n;
                                return (
                                  <button
                                    key={n}
                                    onClick={() => chooseReg(n)}
                                    disabled={choosingReg}
                                    data-testid={`pub-reg-opt-${n}`}
                                    className={`w-full text-left text-xs px-3 py-2 rounded border flex gap-2 transition-colors disabled:opacity-60 ${
                                      active ? "bg-[#c5a76f]/15 border-[#c5a76f]/50 text-white" : "border-white/10 text-white/65 hover:bg-white/5"
                                    }`}
                                  >
                                    <span className={`shrink-0 font-bold ${active ? "text-[#c5a76f]" : "text-white/40"}`}>{active ? "✓" : n}</span>
                                    <span>{o}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {s.registration_choice && (
                              <p className="mt-2 text-[11px] text-[#c5a76f]">
                                {REG[lang].chosen}: {s.registration_choice}
                              </p>
                            )}
                          </div>
                        )}
                        {/* Number plate combination — broker can submit the desired plate */}
                        <div className="mt-3 pt-3 border-t border-white/10" data-testid="pub-plate-section">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-[#c5a76f] text-xs font-medium">{REG[lang].plateTitle}</span>
                            <button
                              type="button"
                              onClick={() => setShowPriceInfo((v) => !v)}
                              data-testid="pub-plate-info-btn"
                              aria-label="Prijsinfo"
                              className="text-white/40 hover:text-[#c5a76f] transition-colors"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {showPriceInfo && (
                            <p className="text-[11px] text-white/70 bg-[#1a1a1a] border border-[#c5a76f]/25 rounded px-2.5 py-2 mb-2" data-testid="pub-plate-price-info">
                              {REG[lang].priceInfo}
                            </p>
                          )}
                          <p className="text-white/55 text-xs leading-relaxed mb-2">{REG[lang].plateHint}</p>
                          <div className="flex gap-2">
                            <input
                              value={plateInput}
                              onChange={(e) => { setPlateInput(e.target.value); setPlateSaved(false); }}
                              placeholder={REG[lang].platePh}
                              data-testid="pub-plate-input"
                              className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#c5a76f]/50"
                            />
                            <button
                              onClick={savePlate}
                              disabled={savingPlate || !plateInput.trim()}
                              data-testid="pub-plate-save"
                              className="px-4 rounded bg-[#c5a76f] text-black text-xs font-medium disabled:opacity-50 flex items-center"
                            >
                              {savingPlate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </button>
                          </div>
                          {plateSaved && (
                            <p className="mt-1.5 text-[11px] text-emerald-400" data-testid="pub-plate-saved">{REG[lang].plateSaved}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {s.key === "extra_werken" && (s.items || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {s.items.map((it) => (
                          <li key={it.id} className="text-xs flex items-center gap-2 text-white/70">
                            <span className={`w-1.5 h-1.5 rounded-full ${it.status === "done" ? "bg-emerald-400" : "bg-white/30"}`} />
                            <span className={it.status === "done" ? "line-through text-white/40" : ""}>{it.description}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.key === "aflevermoment" && s.scheduled_at && (
                      <div className="text-white/70 text-xs mt-1 flex items-center gap-1.5">
                        <CalendarClock className="w-3.5 h-3.5 text-[#c5a76f]" /> {fmtDateTime(s.scheduled_at, loc)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Comments */}
        <div className="mt-6 border-t border-white/8 pt-6">
          <h2 className="text-white/80 text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> {t.questions}
          </h2>
          <div className="space-y-2 mb-4">
            {(d.comments || []).map((cm) => (
              <div key={cm.id} className={`rounded-lg p-3 text-sm ${cm.author === "admin" ? "bg-[#c5a76f]/10 border border-[#c5a76f]/20" : "bg-[#242424] border border-white/8"}`}>
                <div className="flex items-center justify-between text-xs text-white/40 mb-1">
                  <span className={cm.author === "admin" ? "text-[#c5a76f]" : "text-white/70"}>{cm.name}</span>
                  <span>{fmtDateTime(cm.created_at, loc)}</span>
                </div>
                <div className="text-white/85 whitespace-pre-wrap">{cm.text}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.namePh} data-testid="pub-comment-name"
              className="w-full bg-[#242424] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30" />
            <div className="flex gap-2">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={t.msgPh}
                data-testid="pub-comment-input"
                className="flex-1 bg-[#242424] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 resize-none" />
              <button onClick={submitComment} disabled={sending || !text.trim()} data-testid="pub-comment-send"
                className="px-4 rounded bg-[#c5a76f] text-black font-medium disabled:opacity-50 flex items-center">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <footer className="mt-10 text-center text-white/30 text-xs">
          TR Premium Cars · <a href="https://tr-premiumcars.be" className="hover:text-[#c5a76f]">tr-premiumcars.be</a>
        </footer>
      </div>
    </div>
  );
}
